import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import Home from "./routers/Home";
import TeamDisplay from "./routers/TeamDisplay";
import ChatDisplay from "./routers/ChatDisplay";
import { User } from "../types";
import { connectSocket } from "./hooks/websocket";
import Developers from "./routers/Developers";
import InviteDisplay from "./routers/InviteDisplay";
import InviteBot from "./routers/Developers/InviteBot";
import Spinner from "./components/Spinner";
import Privacy from "./routers/Policies/Privacy";
import EmailVerify from "./routers/EmailVerify";
import * as Sentry from "@sentry/react";
import { handleSocket } from "./utils/handleSocket"; 
import toast from "react-hot-toast";
import TemplateDisplay from "./routers/TemplateDisplay";
const ToS = lazy(() => import('./routers/Policies/ToS'));

const handleBasicQueries = () => {
  const userQuery = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const response = await fetch("__SITE_BASE__/api/v1/me", { credentials: 'include' });
      if (!response.ok) return toast.error("Failed to fetch user data");
      return response.json() as Promise<{ user: User }>;
    },
    retry: 1,
    retryDelay: 1000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  }) as any;

  const teamsQuery = useQuery({
    queryKey: ["teams"],
    queryFn: async () => {
      const response = await fetch("__SITE_BASE__/api/v1/teams");
      if (!response.ok) return toast.error("Failed to fetch teams data");
      return response.json();
    },
    retry: 1,
    retryDelay: 1000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: !!userQuery.data?.user,
  });

  const unread = useQuery({
    queryKey: ["unread"],
    queryFn: async () => {
      const response = await fetch("__SITE_BASE__/api/v1/teams/unread");
      if (!response.ok) throw new Error("Failed to fetch unread data");
      return response.json();
    },
    retry: 1,
    retryDelay: 1000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(userQuery.data?.user),
  });

  return { userQuery, teamsQuery, unread };
};

const useWebSocket = (userQuery: any) => {
  const [websocket, setWebsocket] = useState<WebSocket | null>(null);
  const [tryReconnect, setTryReconnect] = useState(0);

  const connectWebSocket = useCallback(async () => {
    if (userQuery.data?.user) {
      const socket = connectSocket("__SITE_BASE__/api/v1/ws", true);
      if (tryReconnect) await new Promise((resolve) => setTimeout(resolve, tryReconnect ? 5000 * tryReconnect : 0));
      if (!socket) return;

      socket.onopen = () => setTryReconnect(0);
      socket.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          if (data.t === "READY") {
            setWebsocket(socket);
            sendPing(socket);
            setInterval(() => sendPing(socket), data.d.heartbeatIntervalMs);
          }
        } catch (error) {
          console.error(error);
        }
      };
      socket.onclose = () => {
        if (tryReconnect < 5) {
          setTryReconnect(tryReconnect + 1);
        }
        setWebsocket(null);
        setTimeout(() => connectWebSocket(), 3000);
      };

      return socket;
    }

    return null;
  }, [userQuery.data?.user, tryReconnect]);

  useEffect(() => {
    if (!websocket) connectWebSocket();
  }, [connectWebSocket, websocket]);

  const sendPing = (ws: WebSocket): void => {
    if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "HEARTBEAT", d: {} }));
  };

  useEffect(() => {
    if (websocket) {
      const interval = setInterval(() => {
        if (websocket.readyState === WebSocket.OPEN) sendPing(websocket);
      }, 25000);
      return () => clearInterval(interval);
    }
  }, [websocket]);

  return websocket;
};

const Router = () => {
  const navigate = useNavigate();
  const { teamId } = useParams();
  const { userQuery, teamsQuery, unread } = handleBasicQueries();
  const websocket = useWebSocket(userQuery);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (userQuery.data?.user) localStorage.setItem("user", JSON.stringify(userQuery.data?.user));
    else localStorage.removeItem("user");
  }, [userQuery.data?.user]);

  useEffect(() => {
    if (!teamId && userQuery.data?.user) {
      //const lastViewedTeamId = localStorage.getItem("lastViewedTeamId");
      //if (lastViewedTeamId) navigate(`/teams/${lastViewedTeamId}`);
    }
  }, [teamId, userQuery.data?.user, navigate]);

  const unreadedMessages = queryClient.getQueryData(["unread"]) as any;

  useEffect(() => {
    if (websocket) {
      const socketHandler = (event: MessageEvent) =>
        handleSocket(event, websocket, userQuery, unreadedMessages, queryClient);

      websocket.addEventListener("message", socketHandler);

      return () => websocket.removeEventListener("message", socketHandler);
    }
  }, [websocket, unreadedMessages, queryClient,userQuery]);

  useEffect(() => {
    if (userQuery.data?.user) {
      Sentry.setUser({
        id: userQuery.data?.user.id,
        username: userQuery.data?.user.username
      });
    }
  }, [userQuery.data?.user]);

  if ((!userQuery.data && !websocket?.OPEN && !teamsQuery.data?.teams && !unread.data)) {
    return <Spinner mainScreen={true} />;
  }

  const loginRequired = (children: any) => {
    if (userQuery.status == "success" && userQuery.data?.user) return children;
    return <Navigate to="/" />;
  };

  return (
    <Routes>
      <Route path="/" element={<Home socket={websocket} />} />
      <Route path="/tos" element={<Suspense fallback={<Spinner mainScreen={true} />}><ToS /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<Spinner mainScreen={true} />}><Privacy /></Suspense>} />
      <Route path="/emailVerify" element={<Suspense fallback={<Spinner mainScreen={true} />}><EmailVerify /></Suspense>} />
      <Route path="/discover/teams" element={loginRequired(<Home socket={websocket} />)} />
      <Route path="/discover/tournaments" element={loginRequired(<Home socket={websocket} />)} />
      <Route path="/me/applications" element={loginRequired(<Home socket={websocket} />)} />
      <Route path="/me/friends" element={loginRequired(<Home socket={websocket} />)} />
      <Route path="/chat" element={loginRequired(<ChatDisplay socket={websocket} />)} />
      <Route path="/chat/:channelId" element={loginRequired(<ChatDisplay socket={websocket} />)} />
      <Route path="/teams/:teamId" element={loginRequired(<TeamDisplay socket={websocket} />)} />
      <Route path="/teams/:teamId/channels/:channelId" element={loginRequired(<TeamDisplay socket={websocket} />)} />
      <Route path="/teams/:teamId/channels/:channelId/applications/:applicationId" element={loginRequired(<TeamDisplay socket={websocket} />)} />
      <Route path="/invite/:id" element={<InviteDisplay query={userQuery} userTeams={teamsQuery} />} />
      <Route path="/template/:code" element={<TemplateDisplay />} />
      <Route path="/developers" element={loginRequired(<Developers query={userQuery} />)} />
      <Route path="/developers/bots/:botId" element={loginRequired(<Developers query={userQuery} />)} />
      <Route path="/b/:botId" element={loginRequired(<InviteBot query={userQuery} />)} />
      <Route path="*" element={<Navigate to={"/"} />} />
    </Routes>
  );
};

export default Router;
